/* Hale Marketing — SVG asset export helpers.
 *
 * Pure-browser conversion: serialize a live <svg> DOM node, render it
 * to a canvas at any pixel size, then trigger a download as PNG or SVG.
 * Zero external dependencies.
 */

const XMLNS = 'http://www.w3.org/2000/svg';

function serializeSvg(svgEl) {
  const clone = svgEl.cloneNode(true);
  // Ensure namespaces are explicit so the rendered XML is valid as a
  // standalone document (required for the <img> blob trick).
  clone.setAttribute('xmlns', XMLNS);
  clone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
  // Inline computed font-family for any child that lacks it — keeps
  // the exported file looking the same when the page CSS isn't loaded.
  return new XMLSerializer().serializeToString(clone);
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so Safari finishes the download
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export function downloadSvg(svgEl, filename) {
  if (!svgEl) return;
  const xml = serializeSvg(svgEl);
  const blob = new Blob([xml], { type: 'image/svg+xml;charset=utf-8' });
  triggerDownload(blob, filename);
}

export async function downloadPng(svgEl, filename, width, height) {
  if (!svgEl) return;
  const xml = serializeSvg(svgEl);
  const blob = new Blob([xml], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  try {
    await new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        // Fill white-ish neutral background underneath in case the asset
        // uses transparency. Hale palette uses IVORY by default, but the
        // asset itself paints its own background rect.
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (pngBlob) => {
            if (!pngBlob) return reject(new Error('toBlob failed'));
            triggerDownload(pngBlob, filename);
            resolve();
          },
          'image/png',
          1.0
        );
      };
      img.onerror = reject;
      img.src = url;
    });
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  }
}

/** Standard preset dimensions used across Hale marketing assets. */
export const PRESETS = {
  presentation: { label: 'Presentation 16:9', w: 1920, h: 1080 },
  website:      { label: 'Website hero',      w: 1600, h: 900  },
  linkedin:     { label: 'LinkedIn 1.91:1',   w: 1200, h: 627  },
  square:       { label: 'Square 1:1',        w: 1080, h: 1080 },
};
